import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, onGridReady } from "src/utils/grid/gridStyleUtils";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultSAL1011_R01DetailDto, SAL1011_R01DetailDto } from "./Dto_SAL1011_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { calculateDetailAmount, SALES_ORDER_DETAIL_AMOUNT_MAPPING } from "src/utils/calculation/calculationUtils";
import { useSAL1011_R01Store } from "./Store_SAL1011_R01";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { isOrderLocked } from "src/utils/order/orderUtils";

const COLUMN_STATE_KEY = 'SAL1011_R01-detail-grid-column-state';


interface Props {
	gridRef: React.RefObject<AgGridReact<SAL1011_R01DetailDto> | null>;
	defaultSAL1011_R01DetailRow: SAL1011_R01DetailDto;
}

export const DetailTable_SAL1011_R01 = (props: Props) => {

	const { gridRef, defaultSAL1011_R01DetailRow } = props;

	const { 
			editSAL1011_R01,
			SAL1011_R01DetailList, setSAL1011_R01DetailList, 
			selectedSAL1011_R01DetailList, setSelectedSAL1011_R01DetailList, removeSelectedSAL1011_R01DetailList, addSelectedSAL1011_R01DetailList, initSelectedSAL1011_R01DetailList,
			deletedSAL1011_R01DetailList, setDeletedSAL1011_R01DetailList, addDeletedSAL1011_R01DetailList, removeDeletedSAL1011_R01DetailList,
			insertSAL1011_R01DetailList, updateSAL1011_R01DetailList, removeSAL1011_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalSAL1011_R01DetailList, setOriginalSAL1011_R01DetailList,
			productResultComplete,
			detailScrollTop, setDetailScrollTop,
		} = useSAL1011_R01Store();
	const isLocked = productResultComplete || isOrderLocked(editSAL1011_R01?.orderStatus);
	const { setCodeSearchModalProps } = useUiuxStore();
	const [clickable, setClickable] = useState(true);

	const { unitList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SAL1011_R01DetailList,
		defaultGridData: defaultSAL1011_R01DetailRow,
		selectedRow: selectedSAL1011_R01DetailList,
		originalGridData: originalSAL1011_R01DetailList,
		updateRow: (index: number, data: any) => updateSAL1011_R01DetailList(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['itemNm', 'itemSpec', 'modelNo', 'orderUnit', 'itemAmt', 'completeTransport'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['itemCd', 'orderQty', 'orderPrice', 'dcAmt'],
		
		pointerCells: ['cancelQty', 'remark'],
		insertedPointerCells: ['itemCd', 'itemNm', 'itemSpec', 'orderUnit', 'orderQty', 'orderPrice', 'dcAmt'],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		orderUnit: '단위',
		orderQty: '수량',
		cancelQty: '취소수량',
		completeTransport: '출하완료수량',
		orderPrice: '단가',
		dcAmt: '할인액',
		itemAmt: '금액',
		remark: '비고',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<SAL1011_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedSAL1011_R01DetailList,
			deletedList: deletedSAL1011_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedSAL1011_R01DetailList, deletedSAL1011_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	// === DetailTable 내장 핸들러 로직 ===
	// 공통 코드 검색 훅 사용 (공통 모달 방식)
const { searchExactItemCode, searchExactCode } = useCodeSearch({});

	// 모듈별 설정
	const SAL1010R01Config = createModuleConfig.SAL1010();

	// 공통 검색 함수를 설정에 주입 (품목 카테고리)
	const fieldConfigWithSearch = {
		...SAL1010R01Config.fieldConfig,
		codeSearchFields: {
			...SAL1010R01Config.fieldConfig.codeSearchFields,
			itemCd: {
				...SAL1010R01Config.fieldConfig.codeSearchFields!.itemCd,
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess)
			}
		}
	};

	// SAL1010 계산 필드 매핑 정의
	const calculateAmount = (data: SAL1011_R01DetailDto): SAL1011_R01DetailDto =>
		calculateDetailAmount(data, SALES_ORDER_DETAIL_AMOUNT_MAPPING);

	// 공통 훅 사용
	const detailTableHandlers = useCommonDetailTableHandlers<SAL1011_R01DetailDto>({
		detailList: SAL1011_R01DetailList,
		updateDetailList: updateSAL1011_R01DetailList,
		originalDetailList: originalSAL1011_R01DetailList,
		setCodeSearchModalProps,
		gridRef,
		fieldConfig: fieldConfigWithSearch,
		codeSearchConfig: SAL1010R01Config.codeSearchConfig,
		calculateAmount,
	});

	/**
	 * 편집 가능한 컬럼 목록 정의
	 * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
	 */
	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'orderUnit', 'orderQty', 'cancelQty', 'orderPrice', 'dcAmt', 'remark'];

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param constraints - 제약 조건 (notTransport 등)
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<SAL1011_R01DetailDto>({
		detailList: SAL1011_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',      // 품목코드
					itemNm: 'itemNm',      // 품명
					itemSpec: 'itemSpec',  // 규격
					modelNo: 'modelNo',    // 품번
					itemUnit: 'orderUnit', // 단위
					itemCost: 'orderPrice' // 단가
				},
				clearFields: {
					// 품목 검색 시 초기화할 필드들
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "",
					orderUnit: "", orderPrice: 0, dcAmt: 0, itemAmt: 0
				},
				searchFunction: searchExactItemCode // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		constraints: {
			notEdit: (params) => {
				if(isLocked) return true;
				if(params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'orderQty',        // 엔터 키 시 수량 필드로 이동
			enableArrowNavigation: true  // 방향키 네비게이션 활성화
		}
	});

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: SAL1011_R01DetailList,
			rowData: SAL1011_R01DetailList[rowIndex],
			insertRow: insertSAL1011_R01DetailList,
			defaultRowData: defaultSAL1011_R01DetailRow,
			setClickable
		});
	}, [SAL1011_R01DetailList, insertSAL1011_R01DetailList, defaultSAL1011_R01DetailRow, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<SAL1011_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

    const baseColumnDefs: ColDef<SAL1011_R01DetailDto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(
			editSAL1011_R01?.salesOrderNo === '' ?
			(params: SAL1011_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: SAL1011_R01DetailDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedSAL1011_R01DetailList,
						selectedList: selectedSAL1011_R01DetailList,
						dataList: SAL1011_R01DetailList
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					SAL1011_R01DetailList, 
					rowData,
					setClickable, 
					removeSAL1011_R01DetailList, 
					(valueIndex) => addDeletedSAL1011_R01DetailList([valueIndex.toString()])
				),
			) : undefined
        ),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 77,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			pasteable: true,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 161,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 127,
			editable: false,
		},
		{
			headerName: '단위', colId: 'orderUnit', field: 'orderUnit', width: 60,
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updateSAL1011_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '수량', colId: 'orderQty', field: 'orderQty', width: 60,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '취소수량', colId: 'cancelQty', field: 'cancelQty', width: 80,
			editable: true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '출하완료수량', colId: 'completeTransport', field: 'completeTransport', width: 100,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '단가', colId: 'orderPrice', field: 'orderPrice', width: 65,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '할인액', colId: 'dcAmt', field: 'dcAmt', width: 65,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '금액', colId: 'itemAmt', field: 'itemAmt', width: 96,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 100,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
	], [
		deletedSAL1011_R01DetailList, SAL1011_R01DetailList, isLocked,
	]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SAL1011_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<SAL1011_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames, isLocked]);
	

	const handleRowClick = useRowSelectionHandler({
		dataList: SAL1011_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedSAL1011_R01DetailList,
		deletedList: deletedSAL1011_R01DetailList,
		addSelected: addSelectedSAL1011_R01DetailList,
		removeSelected: removeSelectedSAL1011_R01DetailList,
		clickable, setClickable
	});

	// 중복 로직 제거: 공통 훅에서 처리됨

	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<SAL1011_R01DetailDto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, SAL1011_R01DetailList, originalSAL1011_R01DetailList, setSAL1011_R01DetailList);
	}, [SAL1011_R01DetailList, originalSAL1011_R01DetailList, ]);

	const pinnedBottomRowData = useMemo(() => {
		const totalQty : number = SAL1011_R01DetailList
			.reduce((sum, item) => sum + (deletedSAL1011_R01DetailList.includes(item.itemCd) ? 0 : Number(item.orderQty) || 0), 0);
		const totalAmt : number = SAL1011_R01DetailList
			.reduce((sum, item) => sum + (deletedSAL1011_R01DetailList.includes(item.itemCd) ? 0 : Number(item.itemAmt) || 0), 0);
		return [{
			itemCd: SAL1011_R01DetailList.length.toLocaleString() + "건",
			orderQty: totalQty,
			itemAmt: totalAmt
		}];	  
	}, [SAL1011_R01DetailList, deletedSAL1011_R01DetailList,]);

	return (
		<GridContextWrapper<SAL1011_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setSAL1011_R01DetailList}
			initSelected={initSelectedSAL1011_R01DetailList}
			defaultRowData={defaultSAL1011_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}	
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
            <Grid_Excel<SAL1011_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}

				// 새로운 훅 구조와 호환
				disableBuiltinCellSelection={false} // 내장 셀 선택 비활성화 (새로운 훅 사용)
				columnStateKey={COLUMN_STATE_KEY} // 자동 컬럼 상태 관리 (localStorage)
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}

				// 행 스타일 규칙 (선택/삭제/수정 상태에 따른 색상)
				rowClassRules={createRowClassRules<SAL1011_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedSAL1011_R01DetailList,
					deletedList: deletedSAL1011_R01DetailList
				})}

				// 이벤트 핸들러들
				onRowClicked={handleRowClick}                                    // 행 클릭 (선택)
				onCellClicked={cellEventHandlers.handleCellClick}               // 셀 클릭 (편집 시작)
                onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={detailTableHandlers.handleCellChange}
                onCellEditingStopped={(e: any) => {
                    // 기존 종료 핸들러 실행
                    detailTableHandlers.handleCellEditingStopped(e);
                    // 값 변경이 없어도 강제 재계산: 수량/취소/단가/할인 컬럼
                    const f = e?.colDef?.field as string | undefined;
                    if (!f) return;
                    if (f === 'orderQty' || f === 'cancelQty' || f === 'orderPrice' || f === 'dcAmt') {
                        const row = e.data as SAL1011_R01DetailDto;
                        if (!row) return;
                        const latestRow = SAL1011_R01DetailList.find((item) => item.valueIndex === row.valueIndex) || row;
                        const originalRow = originalSAL1011_R01DetailList.find((item) => item.valueIndex === row.valueIndex);
                        const normalize = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
                        const hasChanged = originalRow
                            ? normalize((row as any)[f]) !== normalize((originalRow as any)[f])
                            : false;
                        const recalculated = calculateAmount({ ...latestRow, ...row });
                        updateSAL1011_R01DetailList(row.valueIndex, {
                            ...recalculated,
                            isModified: row.inserted ? false : (latestRow.isModified || hasChanged),
                        });
                    }
                }}
				pinnedBottomRowData={pinnedBottomRowData}
                rowDragManaged={true}
                rowDragEntireRow={true}
				onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                onRowDragEnd={(e: any) => {
                    const api = e.api;
                    // 정렬 해제하여 사용자가 정한 순서를 유지 (버전 호환용)
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    const reordered: SAL1011_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,
                            orderIndexSeq: 0,
                        });
                    });
                    setSAL1011_R01DetailList(reordered);
					isRowDraggingRef.current = false;
                }}
				
				// 정렬 후 처리 (드래그 중에는 자동 정렬 방지)
				onPostSortRows={handlePostSort}
			/>
		</GridContextWrapper>
	)
} 
